import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REDIRECT_SECONDS = 3;

const OrganizationFeedbackSuccess = () => {
  const { organizationId } = useParams();
  const location = useLocation();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [redirected, setRedirected] = useState(false);

  const { rating, detailed } = location.state || {};

  useEffect(() => {
    const fetchOrganizationInfo = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/organizations/${organizationId}/public`);
        setOrganization(response.data);
      } catch (error) {
        console.error('Failed to fetch organization info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrganizationInfo();
  }, [organizationId]);

  const isHighRating = rating >= 4;
  const googleUrl = organization?.google_review_url?.trim();

  // Auto-redirect to Google Reviews on high rating, with a visible countdown
  useEffect(() => {
    if (!isHighRating || !googleUrl || redirected) return;
    if (countdown <= 0) {
      setRedirected(true);
      window.location.href = googleUrl;
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, isHighRating, googleUrl, redirected]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const headerColor = isHighRating ? 'bg-green-500' : 'bg-blue-500';
  const headerTextColor = isHighRating ? 'text-green-100' : 'text-blue-100';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">

          {/* Logo on its own white band — full logo, not a circle */}
          {organization?.logo && (
            <div className="bg-white px-6 pt-8 pb-4 flex items-center justify-center" data-testid="success-logo-band">
              <img
                src={organization.logo}
                alt={organization.name}
                className="max-h-24 max-w-[260px] w-auto h-auto object-contain"
              />
            </div>
          )}

          {/* Header (coloured band) */}
          <div className={`${headerColor} px-6 py-8 text-center`}>
            <div className="text-4xl mb-4">{isHighRating ? '🎉' : '🙏'}</div>

            <h1 className="text-xl font-bold text-white mb-2">
              {isHighRating ? 'Thank You!' : 'Thank You for Your Feedback!'}
            </h1>

            <p className={`${headerTextColor} text-sm mb-4`}>
              {isHighRating
                ? "We're thrilled you had a great experience!"
                : 'Your feedback helps us improve our service.'}
            </p>

            <div className="flex justify-center mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`text-2xl ${star <= rating ? 'text-yellow-300' : 'text-white opacity-30'}`}
                >
                  ⭐
                </span>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-8 text-center space-y-6">
            {isHighRating ? (
              <>
                {googleUrl ? (
                  <div className="space-y-4" data-testid="google-redirect-block">
                    <h2 className="text-lg font-semibold text-gray-900">Love our service?</h2>
                    <p className="text-gray-600 text-sm">
                      Help others discover us by sharing your experience on Google Reviews.
                    </p>
                    <p className="text-xs text-gray-500" data-testid="redirect-countdown">
                      {redirected
                        ? 'Redirecting…'
                        : `Redirecting to Google Reviews in ${countdown} second${countdown === 1 ? '' : 's'}…`}
                    </p>
                    <a
                      href={googleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setRedirected(true)}
                      data-testid="google-review-button"
                      className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md font-medium hover:bg-blue-700 transition-colors"
                    >
                      Open Google Review now
                    </a>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="no-google-url-block">
                    <h2 className="text-lg font-semibold text-gray-900">Love our service?</h2>
                    <p className="text-gray-600 text-sm">
                      Help others discover us by sharing your experience on Google Reviews.
                    </p>
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      Tip for admins: set the Google Review URL in your organization settings to auto-redirect happy customers here.
                    </p>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Your feedback has been recorded. Thank you for choosing {organization?.name}!
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-900">We Appreciate Your Honest Feedback</h2>
                  <p className="text-gray-600 text-sm">
                    {detailed
                      ? 'Thank you for taking the time to provide detailed feedback. We will review your comments and work to improve our service.'
                      : 'Your rating has been recorded and will help us serve you better in the future.'}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Your feedback has been recorded. Thank you for choosing {organization?.name}!
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 text-center">
            <p className="text-xs text-gray-500 font-medium">{organization?.name}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationFeedbackSuccess;
